import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useSessionNavigation } from './utils/useSessionNavigation';
import { AppLayout, ProtectedRoute } from './components';
import { AnimatedSplash } from './components/AnimatedSplash';
import { useAuth } from './hooks';
import { homePath } from './utils';
import { Login, OnboardingCallback, PendingApproval, AccountDeleted } from './views/auth';
import { Dashboard } from './views/dashboard';
import { Documents, UploadDocument } from './views/documents';
import { OfficialDocuments } from './views/official-documents';
import { AddWorkExperience, EditWorkExperience, Experience } from './views/experience';
import {
  EditManagerProfile,
  ManagerDashboard,
  SearchWorkers,
  WorkerPassport,
} from './views/manager';
import { DeleteAccount, EditProfile, ExportProfile, Profile } from './views/profile';
import { SharedProfileView, ManageShares, PublicPassportView } from './views/share';
import { AddQualification, EditQualification, Qualifications } from './views/qualifications';
import { AddReference, References } from './views/references';
import { VerifierQueue, VerifierWorkers } from './views/verifier';

const App: React.FC = () => {
  const { isAuthenticated, loading, accountType } = useAuth();
  const sessionEpoch = useSessionNavigation();
  const home = homePath(accountType);
  const [showSplash, setShowSplash] = useState(() => Capacitor.isNativePlatform());

  if (showSplash) {
    return (
      <AnimatedSplash isReady={!loading} onFinish={() => setShowSplash(false)} />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <Routes key={sessionEpoch}>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={home} replace /> : <Login />}
      />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/auth/callback" element={<OnboardingCallback />} />
      <Route path="/pending-approval" element={<PendingApproval />} />
      <Route path="/account-deleted" element={<AccountDeleted />} />
      <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
      <Route path="/share/:token" element={<SharedProfileView />} />
      <Route path="/p/:token" element={<PublicPassportView />} />

      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Routes>
                <Route path="/dashboard" element={
                  accountType === 'verifier'
                    ? <Navigate to="/verifier/queue" replace />
                    : accountType === 'manager'
                      ? <ManagerDashboard />
                      : <Dashboard />
                } />

                <Route path="/profile" element={
                  accountType === 'manager'
                    ? <Navigate to="/manager/profile" replace />
                    : accountType === 'verifier'
                      ? <Navigate to="/verifier/profile" replace />
                      : <Profile />
                } />
                <Route path="/profile/edit" element={
                  accountType === 'manager'
                    ? <Navigate to="/manager/profile/edit" replace />
                    : accountType === 'verifier'
                      ? <Navigate to="/verifier/profile" replace />
                      : <EditProfile />
                } />
                <Route path="/account/delete" element={<DeleteAccount />} />
                <Route path="/profile/cv" element={<ExportProfile />} />
                <Route path="/profile/shares" element={<ManageShares />} />
                <Route path="/qualifications" element={<Qualifications />} />
                <Route path="/qualifications/add" element={<AddQualification />} />
                <Route path="/qualifications/:id/edit" element={<EditQualification />} />
                <Route path="/experience" element={<Experience />} />
                <Route path="/experience/add" element={<AddWorkExperience />} />
                <Route path="/experience/:id/edit" element={<EditWorkExperience />} />
                <Route path="/references" element={<References />} />
                <Route path="/references/add" element={<AddReference />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/documents/upload" element={<UploadDocument />} />
                <Route path="/official-documents" element={<OfficialDocuments />} />

                <Route path="/manager/profile" element={
                  accountType === 'manager' ? <EditManagerProfile /> : <Navigate to={home} replace />
                } />
                <Route path="/manager/profile/edit" element={
                  accountType === 'manager' ? <EditManagerProfile /> : <Navigate to={home} replace />
                } />
                <Route path="/manager/workers" element={
                  accountType === 'manager' ? <SearchWorkers /> : <Navigate to={home} replace />
                } />
                <Route path="/manager/workers/:workerId" element={
                  accountType === 'manager' ? <WorkerPassport mode="manager" /> : <Navigate to={home} replace />
                } />

                <Route path="/verifier/queue" element={
                  accountType === 'verifier' ? <VerifierQueue /> : <Navigate to={home} replace />
                } />
                <Route path="/verifier/workers" element={
                  accountType === 'verifier' ? <VerifierWorkers /> : <Navigate to={home} replace />
                } />
                <Route path="/verifier/workers/:workerId" element={
                  accountType === 'verifier' ? <WorkerPassport mode="verifier" /> : <Navigate to={home} replace />
                } />
                <Route path="/verifier/profile" element={
                  accountType === 'verifier' ? <EditProfile /> : <Navigate to={home} replace />
                } />

                <Route path="/" element={<Navigate to={home} replace />} />
                <Route path="*" element={<Navigate to={home} replace />} />
              </Routes>
            </AppLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
